import React, { useState } from 'react'
import { HStack, VStack } from '@astryxdesign/core/Layout'
import { Text } from '@astryxdesign/core/Text'
import { TextInput } from '@astryxdesign/core/TextInput'
import { Token } from '@astryxdesign/core/Token'
import PillButton from './PillButton'

export const MAX_PUBLICATION_READERS = 100
// Mirrors the server's check in publicationAccess.ts: loose on purpose, it only catches typos.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface PublishReadersFieldProps {
  readers: string[]
  onChange: (readers: string[]) => void
  isDisabled?: boolean
}

/**
 * The email list for a restricted publication: a text input with an Add button (Enter adds too)
 * and the current readers as removable tokens. Addresses are lower-cased and de-duplicated here
 * so what the writer sees is what the server stores.
 */
export default function PublishReadersField({
  readers,
  onChange,
  isDisabled = false
}: PublishReadersFieldProps): React.JSX.Element {
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)
  const atLimit = readers.length >= MAX_PUBLICATION_READERS

  const addDraft = (): void => {
    const email = draft.trim().toLowerCase()
    if (!email) return
    if (!EMAIL_PATTERN.test(email)) {
      setError('Enter a full email address, like reader@example.com.')
      return
    }
    if (readers.includes(email)) {
      setError('That reader is already on the list.')
      return
    }
    if (atLimit) {
      setError(`You can share a story with up to ${MAX_PUBLICATION_READERS} readers.`)
      return
    }
    onChange([...readers, email])
    setDraft('')
    setError(null)
  }

  return (
    <VStack gap={2}>
      <HStack gap={2} vAlign="end">
        <TextInput
          type="email"
          label="Reader email"
          description="Readers sign in with this email to open the story."
          placeholder="reader@example.com"
          value={draft}
          onChange={(value) => {
            setDraft(value)
            if (error) setError(null)
          }}
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              addDraft()
            }
          }}
          status={error ? { type: 'error', message: error } : undefined}
          isDisabled={isDisabled || atLimit}
          disabledMessage={atLimit ? 'The reader list is full.' : undefined}
          size="sm"
        />
        <PillButton
          variant="secondary"
          size="sm"
          label="Add"
          onClick={addDraft}
          isDisabled={isDisabled || atLimit || !draft.trim()}
        />
      </HStack>
      {readers.length > 0 ? (
        <HStack gap={1} wrap="wrap" aria-label="Readers">
          {readers.map((email) => (
            <Token
              key={email}
              label={email}
              size="sm"
              onRemove={
                isDisabled ? undefined : () => onChange(readers.filter((item) => item !== email))
              }
            />
          ))}
        </HStack>
      ) : (
        <Text type="supporting" color="secondary" display="block">
          No readers yet. Until you add some, only you can open the story.
        </Text>
      )}
    </VStack>
  )
}
